import {Avatar, Tooltip} from 'platform/components';
import {Box, Hide, HStack, Icon, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageResponseBody} from '@dms/api/core';
import {i18n} from '@dms/i18n';

import {DOT_CHARACTER, formatFileSize, parseDate} from 'shared/web';

import {useThreadMessageAuthor} from '../hooks/useThreadMessageAuthor';
import {MessageContextLink} from '../types/MessageContextLink';
import {ThreadReadState} from '../types/ThreadReadState';
import {isCustomerMessageDeliveryError} from '../utils/isCustomerMessageDeliveryError';
import {ThreadContextLinks} from './ThreadContextLinks';
import {ThreadReadStateControls} from './ThreadReadStateControls';

interface ThreadMessageBubbleProps {
  message: CustomerMessageResponseBody;
  contextLinks: MessageContextLink[];
  showOutgoingRecipient?: boolean;
  /** Enables the "new" marker and the manual read toggle; without it the bubble shows neither. */
  readState?: ThreadReadState;
}

export function ThreadMessageBubble(props: ThreadMessageBubbleProps) {
  const formatDateTime = useDateTimeFormatter();

  const isOutgoing = props.message.direction === 'OUTGOING';
  const isDeliveryError = isCustomerMessageDeliveryError(props.message.state);
  const isOnDarkBackground = isOutgoing || isDeliveryError;

  const bubbleBackgroundColor = match<
    {isDeliveryError: boolean; isOutgoing: boolean},
    ThemeColorPath
  >({isDeliveryError, isOutgoing})
    .with({isDeliveryError: true}, () => 'palettes.red.60.100')
    .with({isOutgoing: true}, () => 'palettes.neutral.80.100')
    .otherwise(() => 'palettes.neutral.20.100');

  const {authorName} = useThreadMessageAuthor(props.message, props.showOutgoingRecipient);

  const formattedPhone = formatPhoneNumber(props.message.contact) ?? props.message.contact;
  const messageSentAtLabel = `${props.message.channel === 'sms' ? i18n.t('general.labels.sms') : i18n.t('general.labels.whatsapp')} ${DOT_CHARACTER} ${formatDateTime('timeShort', parseDate(props.message.createdAt))}`;

  return (
    <VStack align={isOutgoing ? 'flex-end' : 'flex-start'} spacing={1}>
      <HStack align="flex-start" spacing={1} justify={isOutgoing ? 'flex-end' : 'flex-start'}>
        <Hide when={isOutgoing}>
          <Avatar name={authorName} size="small" />
        </Hide>

        <VStack spacing={1} align={isOutgoing ? 'flex-end' : 'flex-start'}>
          <Show when={isNotNilOrEmpty(props.contextLinks)}>
            <ThreadContextLinks links={props.contextLinks} />
          </Show>

          <Show when={isDeliveryError}>
            <HStack spacing={1} align="center">
              <Icon value="alert/error" size={4} color="severity.danger" />
              <Text size="xSmall" color="danger" alternative>
                {i18n.t('general.labels.messageNotDelivered')}
              </Text>
            </HStack>
          </Show>

          <Box
            maxWidth="70%"
            minWidth={40}
            paddingVertical={2}
            paddingHorizontal={3}
            borderBottomLeftRadius="medium"
            borderBottomRightRadius="medium"
            borderTopLeftRadius={isOutgoing ? 'medium' : undefined}
            borderTopRightRadius={isOutgoing ? undefined : 'medium'}
            backgroundColor={bubbleBackgroundColor}
          >
            <VStack spacing={2}>
              <HStack spacing={1} width="100%" justify="flex-end">
                <Tooltip
                  label={formattedPhone}
                  isDisabled={isNilOrEmpty(formattedPhone)}
                  placement={isOutgoing ? 'left' : 'right'}
                >
                  <Text size="xSmall" alternative color={isOnDarkBackground ? 'white' : 'primary'}>
                    {authorName}
                  </Text>
                </Tooltip>
              </HStack>

              <Text size="small" color={isOnDarkBackground ? 'white' : 'primary'} preWrap>
                {props.message.content}
              </Text>

              {props.message.attachments.map((attachment) => (
                <HStack key={attachment.uri} spacing={1} align="center">
                  <Icon
                    value="action/description"
                    size={4}
                    color={isOnDarkBackground ? 'palettes.neutral.40.100' : 'text.secondary'}
                  />
                  <Text size="xSmall" color={isOnDarkBackground ? 'white' : 'secondary'}>
                    {isNotNilOrEmpty(attachment.size) && attachment.size
                      ? `${attachment.filename} (${formatFileSize(attachment.size)})`
                      : attachment.filename}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          <HStack spacing={2} align="center">
            <Text size="xxSmall" color="secondary">
              {messageSentAtLabel}
            </Text>
            <ThreadReadStateControls message={props.message} readState={props.readState} />
          </HStack>
        </VStack>

        <Show when={isOutgoing}>
          <Avatar name={authorName} size="small" />
        </Show>
      </HStack>
    </VStack>
  );
}
